import { useState } from "react";
import { Plus, Trash2, Boxes } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { SectionCard, Segmented, InfoNote, Row } from "@/components/common/Cards";
import { Field, MoneyInput, NumInput, TextInput } from "@/components/common/Inputs";
import { Sel } from "@/components/common/Sel";
import { evaluatePackage, newPackage } from "@/lib/engines/hosting";
import { fmtNum, fmtRp, fmtPct } from "@/lib/format";

export default function PackagesPanel({ packages, setPackages, params, fleet, basis, setBasis, basisResolved, readOnly }) {
  const serverOptions = [
    { value: "pool", label: "Rata-rata semua server (pool)" },
    ...fleet.compute.map((r) => ({ value: r.server.id, label: `${r.server.hostname} · ${r.server.location}` })),
  ];

  const add = () => setPackages((p) => [...p, newPackage({ name: `Paket Baru #${p.length + 1}` })]);
  const remove = (id) => setPackages((p) => p.filter((x) => x.id !== id));
  const update = (id, patch) => setPackages((p) => p.map((x) => (x.id === id ? { ...x, ...patch } : x)));

  return (
    <div className="space-y-5">
      <SectionCard title="Paket & basis biaya" eyebrow="Package COGS" testid="hosting-packages-card">
        <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-end mb-4">
          <Field label="Basis perhitungan" hint="Pakai rata-rata semua server, atau simulasikan terhadap 1 server tertentu.">
            <Sel value={basis} onChange={setBasis} options={serverOptions} testid="hosting-basis-select" />
          </Field>
          <Button onClick={add} disabled={readOnly} data-testid="hosting-add-package-button">
            <Plus className="h-4 w-4" /> Tambah paket
          </Button>
        </div>

        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3 text-xs mb-4">
          <Row label="Biaya CPU per vCPU/bulan" value={fmtRp(basisResolved.unit.cpu)} />
          <Row label="Biaya RAM per GB/bulan" value={fmtRp(basisResolved.unit.ram)} />
          <Row label="Biaya Disk per GB/bulan" value={fmtRp(basisResolved.unit.disk)} />
          <Row label="Biaya IPv4 / bulan" value={fmtRp(basisResolved.ipPrice)} muted />
          <Row label="Biaya Mbps / bulan" value={fmtRp(basisResolved.bwPrice)} muted />
          <Row label="Target margin" value={fmtPct(params.marginPct)} muted />
        </div>

        <div className="space-y-4">
          {packages.map((pkg) => {
            const res = evaluatePackage(pkg, basisResolved, params);
            return (
              <div key={pkg.id} className="rounded-xl border border-border bg-[hsl(220_12%_7%)] p-4" data-testid={`hosting-package-${pkg.id}`}>
                <div className="grid lg:grid-cols-[1.3fr_1fr] gap-4">
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Input
                        value={pkg.name}
                        onChange={(e) => update(pkg.id, { name: e.target.value })}
                        className="h-9 bg-[hsl(220_12%_8%)] font-medium flex-1 min-w-[160px]"
                        disabled={readOnly}
                        data-testid={`hosting-package-${pkg.id}-name`}
                      />
                      <Segmented
                        value={pkg.type}
                        onChange={(v) => update(pkg.id, { type: v })}
                        options={[
                          { value: "vps", label: "VPS" },
                          { value: "shared", label: "Shared" },
                          { value: "dedicated", label: "Dedicated" },
                        ]}
                        size="sm"
                        testid={`hosting-package-${pkg.id}-type`}
                        disabled={readOnly}
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-9 w-9 text-muted-foreground hover:text-[hsl(0_72%_62%)]"
                        onClick={() => remove(pkg.id)}
                        disabled={readOnly}
                        data-testid={`hosting-package-${pkg.id}-remove`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {pkg.type !== "shared" && (
                        <>
                          <Field label="vCPU">
                            <NumInput value={pkg.vcpu} onChange={(v) => update(pkg.id, { vcpu: v })} decimals={0} suffix="vCPU" disabled={readOnly} testid={`hosting-package-${pkg.id}-vcpu`} />
                          </Field>
                          <Field label="RAM">
                            <NumInput value={pkg.ramGb} onChange={(v) => update(pkg.id, { ramGb: v })} decimals={1} suffix="GB" disabled={readOnly} testid={`hosting-package-${pkg.id}-ram`} />
                          </Field>
                        </>
                      )}
                      <Field label="Disk">
                        <NumInput value={pkg.diskGb} onChange={(v) => update(pkg.id, { diskGb: v })} decimals={0} suffix="GB" disabled={readOnly} testid={`hosting-package-${pkg.id}-disk`} />
                      </Field>
                      {pkg.type === "shared" && (
                        <Field label="Pemakaian disk aktual">
                          <NumInput value={pkg.diskUsagePct} onChange={(v) => update(pkg.id, { diskUsagePct: v })} suffix="%" decimals={0} disabled={readOnly} testid={`hosting-package-${pkg.id}-diskusage`} />
                        </Field>
                      )}
                      {pkg.type !== "dedicated" && (
                        <Field label="Bandwidth kuota">
                          <NumInput value={pkg.bandwidthTb} onChange={(v) => update(pkg.id, { bandwidthTb: v })} suffix="TB" decimals={1} disabled={readOnly} testid={`hosting-package-${pkg.id}-bw`} />
                        </Field>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {pkg.type === "shared" && (
                        <>
                          <Field label="Akun / vCPU" hint="Perkiraan akun hosting yang bisa berbagi 1 vCPU.">
                            <NumInput value={pkg.accountsPerVcpu} onChange={(v) => update(pkg.id, { accountsPerVcpu: v })} decimals={0} disabled={readOnly} testid={`hosting-package-${pkg.id}-accvcpu`} />
                          </Field>
                          <Field label="Akun / GB RAM">
                            <NumInput value={pkg.accountsPerGbRam} onChange={(v) => update(pkg.id, { accountsPerGbRam: v })} decimals={0} disabled={readOnly} testid={`hosting-package-${pkg.id}-accram`} />
                          </Field>
                        </>
                      )}
                      <Field label="IPv4">
                        <NumInput value={pkg.ipv4} onChange={(v) => update(pkg.id, { ipv4: v })} decimals={0} disabled={readOnly} testid={`hosting-package-${pkg.id}-ipv4`} />
                      </Field>
                      <Field label="Lisensi/bulan">
                        <MoneyInput value={pkg.licenseMonthly} onChange={(v) => update(pkg.id, { licenseMonthly: v })} disabled={readOnly} testid={`hosting-package-${pkg.id}-license`} />
                      </Field>
                      <Field label="Harga jual (opsional)" hint="Kosongkan untuk pakai harga saran berdasarkan target margin.">
                        <MoneyInput value={pkg.price} onChange={(v) => update(pkg.id, { price: v })} disabled={readOnly} testid={`hosting-package-${pkg.id}-price`} />
                      </Field>
                    </div>
                  </div>
                  <div className="rounded-lg border border-[color:var(--hairline)] bg-[hsl(43_30%_7%)] p-4 space-y-2 text-sm">
                    <div className="flex items-baseline justify-between">
                      <span className="eyebrow">COGS / bulan</span>
                      <span className="num text-primary text-lg" data-testid={`hosting-package-${pkg.id}-cogs`}>{fmtRp(res.cogs)}</span>
                    </div>
                    <Row label="Resource (CPU+RAM+Disk)" value={fmtRp(res.parts.resource || res.parts.cpu + res.parts.ram + res.parts.disk || 0)} muted />
                    <Row label="IP" value={fmtRp(res.parts.ip)} muted />
                    <Row label="Bandwidth dedicated" value={fmtRp(res.parts.bandwidth)} muted />
                    <Row label="Lisensi + support" value={fmtRp(res.parts.license + res.parts.support)} muted />
                    <Row label="Harga jual saran" value={fmtRp(res.suggested)} />
                    <Row label="Harga berlaku" value={fmtRp(res.price)} strong />
                    <Row label="Margin aktual" value={fmtPct(res.marginActual)} />
                    <div className="flex items-center gap-2 pt-1">
                      <Badge variant="outline" className="border-[color:var(--hairline)] text-[10px] text-[hsl(43_60%_66%)]" data-testid={`hosting-package-${pkg.id}-cap`}>
                        Kapasitas: {res.usersAtTarget === Infinity ? "-" : fmtNum(res.usersAtTarget)} akun/server
                      </Badge>
                      <Badge variant="outline" className="border-border text-[10px] text-muted-foreground">
                        Bottleneck: {res.bottleneck}
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          {packages.length === 0 && (
            <div className="text-center p-10 border border-dashed border-border rounded-xl">
              <Boxes className="h-6 w-6 text-muted-foreground mx-auto" />
              <p className="text-sm text-muted-foreground mt-2">Belum ada paket. Tambah paket baru untuk mulai menghitung COGS.</p>
            </div>
          )}
        </div>
      </SectionCard>
    </div>
  );
}
